import _ from 'lodash'
import EventBus from '../bus'
import Vue from 'vue'
import { DEFAULT_LANG_CODE } from '../utils/lang'

import { setCurrentUserObject } from '../utils/user'

/**
 * @var {ApiMiddleware}
 */
let api = {}

/**
 * Set root path for api
 * @param {ApiMiddleware} middlewareObject
 */
export const setCurrentUserApiMiddleware = middlewareObject => {
    api = middlewareObject
}

export const EVENT_USER_LOGGED_IN = '/currentUser/loggedIn'
export const EVENT_USER_LOGGED_OUT = '/currentUser/loggedOut'

// odpali se kdykoliv kdyz neco nastavi platneho uzivatele
export const EVENT_USER_SET = '/currentUser/set'
// odpali se kdykoliv kdyz neco odnastavi uzivatele (napr z duvodu neplatnosti etc..)
export const EVENT_USER_UNSET = '/currentUser/unset'

export const EVENT_USER_TOKEN_SET = '/currentUser/tokenSet'
export const EVENT_USER_TOKEN_UNSET = '/currentUser/tokenUnset'
export const EVENT_USER_TOKEN_TIMEOUT = '/currentUser/tokenTimeout'

export const EVENT_USER_CHANGED_LOCALE = '/currentUser/changedLocale'

const MODULES = {
    comments: false,
    attachments: false,
    rainbow: true,
    files: {
        enabled: false,
        basePath: '//ASD',
        baseFolderProperty: null,
        hostname: null,
        protocol: null,
        port: 5000,
        noteProperty: null,
    },
}
// gui.labels.tasks
export const DEFAULT_SETTINGS = {
    gui: {
        projectsPaging: 20,
        tasksPaging: 20,
        resourcesPaging: 20,
        gantt: {
            milestonesFirst: false,
            minTaskLength: 3,
            milestonesEnabled: false,
            manualSave: false,
        },
        labels: {
            tasks: ['system-nazev'],
            projects: ['system-nazev'],
            resources: ['system-nazev', 'system-jmeno', 'system-prijmeni'],
        },
        sorter: {
            tasks: { column: '_type', order: 'asc' },
            projects: { column: '_type', order: 'asc' },
            resources: { column: '_type', order: 'asc' },
        },
        pages: {
            dashboard: {
                showGantt: false,
                showProjects: false,
            },
        },
    },
    modules: MODULES,
}

export default () => ({
    state: {
        currentUser: {},
        company: {},
        langCode: DEFAULT_LANG_CODE,
        apiInfo: {},
    },
    getters: {
        isLoggedIn: state => !!_.get(state.currentUser, 'token', false),
        hasCompany: state => !!_.get(state.currentUser, 'company.id', null),
        currentLangCode: state => state.langCode,
        currentUser: state => state.currentUser,
        currentUserId: state => _.get(state, 'currentUser.id', undefined),
        dateFormat: state => 'D.M.Y HH:mm',
        settings: (state, getters) => {
            const companySettings = _.get(getters.currentUser, 'company.settings', {})
            return _.merge(DEFAULT_SETTINGS, companySettings)
        },
        modules: (state, getters) => getters.settings.modules,
        apiInfo: state => state.apiInfo,
        groupedActions: (state, getters) => {
            return _.chain(getters.currentUser.groups)
                .cloneDeep()
                .map(g => g.actions)
                .flatten()
                .filter(g => _.size(g) > 0)
                .value()
        },
        currentUserHasAction: (state, getters) => action => getters.groupedActions.includes(action),
    },
    mutations: {
        setCurrentUser: (state, currentUser) => {
            state.currentUser = currentUser
            if (_.isObject(currentUser)) {
                EventBus.$emit(EVENT_USER_SET, currentUser)
            } else {
                EventBus.$emit(EVENT_USER_UNSET)
            }
            return state
        },
        setLangCode: (state, langCode) => {
            localStorage.setItem('lang', langCode)
            state.langCode = langCode
            EventBus.$emit(EVENT_USER_CHANGED_LOCALE, langCode)
            return state
        },
        setSettings: (state, settings) => {
            localStorage.userSettings = JSON.stringify(settings)
        },
        setCompanySync: (state, entries) => {
            _.set(state.currentUser, 'company.settings.sync', entries)
            return state
        },
        setApiInfo: (state, info) => {
            state.apiInfo = info
            return state
        },
    },
    actions: {
        /**
         * Autentification data
         * @param {Object} data
         */
        async currentUserLogin({ commit }, data) {
            return api.post('/auth', data)
                .then(async response => {
                    const token = response.token
                    localStorage.setItem('auth_token', token)
                    EventBus.$emit(EVENT_USER_TOKEN_SET, token)
                    EventBus.$emit(EVENT_USER_LOGGED_IN, response.data)
                    const user = _.first(await api.get(`/user/${Vue.$jwt.decode(token).uid}?depth=2`))
                    commit('setCurrentUser', setCurrentUserObject(user, token))
                    return response.data
                })
                .catch(e => {
                    return Promise.reject(e)
                })
        },
        async getUserId({ commit }) {
            if (localStorage.auth_token) {
                EventBus.$emit(EVENT_USER_TOKEN_SET, localStorage.auth_token)
                const user = _.first(await api.get(`/user/${Vue.$jwt.decode(localStorage.auth_token).uid}?depth=2`))
                return user.id
            }
        },
        async saveCurrentUserCompanySyncs({ commit, rootGetters }, data) {
            const company = _.get(rootGetters.currentUser, 'company')
            _.set(company, 'settings.sync', data)
            await api.put(`/company/${company.id}`, company)
            commit('setCompanySync', data)
        },
        async saveCurrentUserCompanySettings({ commit, rootGetters }, data) {
            const company = _.get(rootGetters.currentUser, 'company')
            Vue.set(company, 'settings', data)
            await api.put(`/company/${company.id}`, company)
        },
        async currentUserLogout({ commit }) {
            return api.get('/logout')
                .then(response => {
                    EventBus.$emit(EVENT_USER_LOGGED_OUT, response.data)
                    commit('setCurrentUser', setCurrentUserObject({}, null))
                    return response
                })
                .finally(_ => {
                    EventBus.$emit(EVENT_USER_TOKEN_UNSET)
                    localStorage.removeItem('auth_token')
                })
        },
        async currentUserCheck({ commit }) {
            if (localStorage.auth_token) {
                EventBus.$emit(EVENT_USER_TOKEN_SET, localStorage.auth_token)
                try {
                    const response = await api.get(`/user/${Vue.$jwt.decode(localStorage.auth_token).uid}?depth=2`)
                    const user = _.first(response)
                    commit('setCurrentUser', setCurrentUserObject(user, localStorage.auth_token))
                } catch (e) {
                    delete localStorage.auth_token
                    commit('setCurrentUser', setCurrentUserObject({}, undefined))
                    console.warn(e)
                }
            }
        },
        async currentUserTokenTimeout({ commit }) {
            commit('setCurrentUser', setCurrentUserObject({}, undefined))
            EventBus.$emit(EVENT_USER_TOKEN_UNSET)
        },
        async apiInfoLoad({ commit }) {
            const info = await api.get('/info')
            commit('setApiInfo', info)
        },
    },
})
